import React, { useState } from 'react';
import {
  Box,
  Card,
  CardContent,
  TextField,
  Button,
  Typography,
  Avatar,
  Grid,
  Paper,
  InputAdornment,
  IconButton,
  Chip,
  Container,
  useMediaQuery,
  useTheme,
} from '@mui/material';
import {
  Visibility,
  VisibilityOff,
  Person,
  Lock,
  Hotel,
  AdminPanelSettings,
  SupervisorAccount,
  PersonOutline,
} from '@mui/icons-material';

const demoCredentials = [
  {
    role: 'Admin',
    email: 'admin@hotelops.com',
    password: 'admin123',
    icon: <AdminPanelSettings />,
    color: '#1976d2',
    description: 'Full system access',
  },
  {
    role: 'Manager',
    email: 'manager@hotelops.com',
    password: 'manager123',
    icon: <SupervisorAccount />,
    color: '#2e7d32',
    description: 'Management dashboard',
  },
  {
    role: 'Staff',
    email: 'staff@hotelops.com',
    password: 'staff123',
    icon: <Person />,
    color: '#ed6c02',
    description: 'Staff operations',
  },
  {
    role: 'Customer',
    email: 'customer@hotelops.com',
    password: 'customer123',
    icon: <PersonOutline />,
    color: '#9c27b0',
    description: 'Customer portal',
  },
];

export default function Login({ onLogin }) {
  const [email, setEmail] = useState('');
  const [password, setPassword] = useState('');
  const [showPassword, setShowPassword] = useState(false);
  const [error, setError] = useState('');
  const theme = useTheme();
  const isMobile = useMediaQuery(theme.breakpoints.down('sm'));
  const isTablet = useMediaQuery(theme.breakpoints.down('md'));

  const handleCredentialClick = (credential) => {
    setEmail(credential.email);
    setPassword(credential.password);
    setError('');
  };

  const handleLogin = (e) => {
    e.preventDefault();
    setError('');

    const user = demoCredentials.find(
      (cred) => cred.email === email && cred.password === password
    );

    if (user) {
      onLogin(user.role);
    } else {
      setError('Invalid email or password');
    }
  };

  return (
    <Box
      sx={{
        minHeight: '100vh',
        background: 'linear-gradient(135deg, #1a237e 0%, #0d47a1 50%, #01579b 100%)',
        display: 'flex',
        alignItems: 'center',
        py: { xs: 2, sm: 4 },
      }}
    >
      <Container maxWidth="lg">
        <Grid container spacing={{ xs: 2, md: 4 }} alignItems="center">
          {/* Left Side - Branding */}
          <Grid item xs={12} md={6}>
            <Box
              sx={{
                textAlign: { xs: 'center', md: 'left' },
                color: 'white',
                mb: { xs: 2, md: 0 },
              }}
            >
              <Box display="flex" alignItems="center" gap={2} justifyContent={{ xs: 'center', md: 'flex-start' }} mb={3}>
                <Avatar
                  sx={{
                    bgcolor: 'white',
                    color: '#1976d2',
                    width: { xs: 60, sm: 80 },
                    height: { xs: 60, sm: 80 },
                  }}
                >
                  <Hotel sx={{ fontSize: { xs: 35, sm: 45 } }} />
                </Avatar>
                <Typography
                  variant={isMobile ? 'h4' : 'h3'}
                  fontWeight="bold"
                  sx={{ textShadow: '2px 2px 4px rgba(0,0,0,0.3)' }}
                >
                  HotelOpsPro
                </Typography>
              </Box>
              <Typography variant={isMobile ? 'h6' : 'h5'} gutterBottom sx={{ opacity: 0.95 }}>
                Complete Hotel Management System
              </Typography>
              <Typography variant="body1" sx={{ opacity: 0.85, maxWidth: 500, mx: { xs: 'auto', md: 0 } }}>
                Streamline your hotel operations with our comprehensive management platform.
                Manage bookings, rooms, staff, and more with ease.
              </Typography>

              {/* Features */}
              {!isMobile && (
                <Box mt={4}>
                  <Grid container spacing={2}>
                    {['Real-time Analytics', 'Staff Management', 'Smart Bookings', 'Inventory Control'].map((feature) => (
                      <Grid item xs={6} key={feature}>
                        <Box display="flex" alignItems="center" gap={1}>
                          <Box
                            sx={{
                              width: 8,
                              height: 8,
                              borderRadius: '50%',
                              bgcolor: 'white',
                            }}
                          />
                          <Typography variant="body2">{feature}</Typography>
                        </Box>
                      </Grid>
                    ))}
                  </Grid>
                </Box>
              )}
            </Box>
          </Grid>

          {/* Right Side - Login Form */}
          <Grid item xs={12} md={6}>
            <Card
              sx={{
                maxWidth: 500,
                mx: 'auto',
                boxShadow: '0 20px 60px rgba(0,0,0,0.3)',
                borderRadius: 3,
              }}
            >
              <CardContent sx={{ p: { xs: 3, sm: 4 } }}>
                <Typography variant={isMobile ? 'h5' : 'h4'} fontWeight="bold" gutterBottom textAlign="center">
                  Welcome Back
                </Typography>
                <Typography variant="body2" color="text.secondary" textAlign="center" mb={3}>
                  Sign in to access your dashboard
                </Typography>

                {/* Demo Credentials */}
                <Paper sx={{ p: 2, mb: 3, bgcolor: '#f5f7fa' }}>
                  <Typography variant="caption" fontWeight="bold" color="text.secondary" gutterBottom display="block">
                    DEMO CREDENTIALS - CLICK TO AUTO-FILL
                  </Typography>
                  <Grid container spacing={1} mt={0.5}>
                    {demoCredentials.map((cred) => (
                      <Grid item xs={6} sm={6} key={cred.role}>
                        <Paper
                          sx={{
                            p: { xs: 1, sm: 1.5 },
                            cursor: 'pointer',
                            border: '2px solid transparent',
                            transition: 'all 0.2s',
                            '&:hover': {
                              borderColor: cred.color,
                              transform: 'translateY(-2px)',
                              boxShadow: `0 4px 12px ${cred.color}30`,
                            },
                          }}
                          onClick={() => handleCredentialClick(cred)}
                        >
                          <Box display="flex" alignItems="center" gap={1} mb={0.5}>
                            <Avatar sx={{ bgcolor: cred.color, width: 28, height: 28 }}>
                              {React.cloneElement(cred.icon, { sx: { fontSize: 16 } })}
                            </Avatar>
                            <Typography variant="body2" fontWeight="bold">
                              {cred.role}
                            </Typography>
                          </Box>
                          <Typography 
                            variant="caption" 
                            color="text.secondary" 
                            display="block" 
                            sx={{ 
                              fontSize: { xs: '0.65rem', sm: '0.75rem' },
                              wordBreak: 'break-all',
                              overflowWrap: 'anywhere',
                            }}
                          >
                            {cred.email}
                          </Typography>
                          <Typography 
                            variant="caption" 
                            color="text.secondary" 
                            display="block" 
                            sx={{ 
                              fontSize: { xs: '0.65rem', sm: '0.75rem' },
                              wordBreak: 'break-all',
                            }}
                          >
                            {cred.password}
                          </Typography>
                        </Paper>
                      </Grid>
                    ))}
                  </Grid>
                </Paper>

                {/* Login Form */}
                <form onSubmit={handleLogin}>
                  <TextField
                    fullWidth
                    label="Email Address"
                    type="email"
                    value={email}
                    onChange={(e) => setEmail(e.target.value)}
                    margin="normal"
                    required
                    InputProps={{
                      startAdornment: (
                        <InputAdornment position="start">
                          <Person color="action" />
                        </InputAdornment>
                      ),
                    }}
                  />
                  <TextField
                    fullWidth
                    label="Password"
                    type={showPassword ? 'text' : 'password'}
                    value={password}
                    onChange={(e) => setPassword(e.target.value)}
                    margin="normal"
                    required
                    InputProps={{
                      startAdornment: (
                        <InputAdornment position="start">
                          <Lock color="action" />
                        </InputAdornment>
                      ),
                      endAdornment: (
                        <InputAdornment position="end">
                          <IconButton
                            onClick={() => setShowPassword(!showPassword)}
                            edge="end"
                          >
                            {showPassword ? <VisibilityOff /> : <Visibility />}
                          </IconButton>
                        </InputAdornment>
                      ),
                    }}
                  />

                  {error && (
                    <Typography color="error" variant="body2" mt={2} textAlign="center">
                      {error}
                    </Typography>
                  )}

                  <Button
                    type="submit"
                    variant="contained"
                    fullWidth
                    size="large"
                    sx={{
                      mt: 3,
                      mb: 2,
                      py: 1.5,
                      background: 'linear-gradient(135deg, #1976d2 0%, #1565c0 100%)',
                      fontSize: '1rem',
                      fontWeight: 'bold',
                      '&:hover': {
                        background: 'linear-gradient(135deg, #1565c0 0%, #0d47a1 100%)',
                      },
                    }}
                  >
                    Sign In
                  </Button>

                  <Box textAlign="center">
                    <Typography variant="caption" color="text.secondary">
                      Demo Mode - No actual authentication required
                    </Typography>
                  </Box>
                </form>
              </CardContent>
            </Card>

            {/* Footer */}
            <Typography variant="caption" color="white" display="block" textAlign="center" mt={2} sx={{ opacity: 0.8 }}>
              © 2025 HotelOpsPro. All rights reserved.
            </Typography>
          </Grid>
        </Grid>
      </Container>
    </Box>
  );
}
